<template>
  <div v-if="book" class="d-flex justify-center justify-lg-end">
    <div v-if="book.quotaOrder.commonInfo.remoteInteract" class="d-flex align-center text-green paragraph-m font-600">
      <e-icon name="check" class="mr-2" />
      Отправлено в менеджер каналов
    </div>

    <template v-else>
      <confirmation-dialog
        v-if="!isOperationHidden(book.quotaOrder.operations.avoid)"
        :action="cancelBook"
        confirm-text="Аннулировать"
        confirm-btn-variant="accent"
      >
        <template #activator>
          <operation-button
            :operation="book.quotaOrder.operations.avoid"
            :loading="isAvoidLoading"
            variant="ghost"
            class="mr-1 mr-sm-3 text-red"
          >
            <template #prepend>
              <e-icon name="block" />
            </template>
            Аннулировать
            <template v-if="!$vuetify.display.smAndDown">Заказ</template>
          </operation-button>
        </template>

        <div class="h3 mb-6">Аннулирование бронирования</div>
        <div class="paragraph-l text-secondary">
          Бронирование № {{ book.quotaOrder.orderInfo.number }}/{{ book.quotaOrder.orderInfo.subNumber }} будет
          аннулировано
        </div>
      </confirmation-dialog>

      <operation-button
        :operation="book.quotaOrder.operations.confirm"
        :loading="isAcceptLoading"
        variant="primary"
        @click="acceptBook"
      >
        Подтвердить
        <template v-if="!$vuetify.display.smAndDown">Заказ</template>
      </operation-button>

      <div v-if="book.quotaOrder.orderInfo.sentToSupplier" class="d-flex align-center text-green paragraph-m font-600">
        <e-icon name="check" class="mr-2" />
        Обработан
      </div>
      <operation-button
        v-else
        variant="primary"
        :operation="book.quotaOrder.operations.sendToSupplier"
        prepend-icon-src="check"
        :loading="isSendLoading"
        @click="sendBook"
      >
        Обработан
      </operation-button>
    </template>
  </div>

  <alert-dialog
    :model-value="orderActionErrorType === errorTypes.OrderIsConfirmedError"
    @update:modelValue="orderActionErrorType = undefined"
  >
    <div class="paragraph-l text-secondary">
      Заказ проживания был
      <b class="text-green">ранее подтверждён</b>
      <br />
      Вы можете изменить его статус при необходимости через Поддержку
    </div>
  </alert-dialog>

  <alert-dialog
    :model-value="orderActionErrorType === errorTypes.OrderIsAvoidedError"
    @update:modelValue="orderActionErrorType = undefined"
  >
    <div class="paragraph-l text-secondary">
      Заказ проживания был
      <b class="text-red">ранее аннулирован</b>
      <br />
      Вы можете изменить его статус при необходимости через Поддержку
    </div>
  </alert-dialog>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue';
import { useRoute } from 'vue-router';
import OperationButton from '@/core/components/buttons/OperationButton.vue';
import AlertDialog from '@/core/components/modals/AlertDialog.vue';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import { EIcon } from '@/core/components/ui';
import { isOperationHidden } from '@/core/utils/permissions';
import { useBooksStore } from '@/parts/books/store/books';
import { errorTypes } from '@/parts/errors/utils/error-types';

const { avoidBook, book, confirmBook, getBook, sendOrderToSupplier } = useBooksStore();

const $route = useRoute();

const orderId = toRef(() => +$route.params.b_id);

const orderActionErrorType = ref<string>();

const processBookActionError = async (
  errors: {
    message: string;
    __typename: string;
  }[],
) => {
  await getBook({ orderId: orderId.value }, true);

  setTimeout(() => {
    orderActionErrorType.value = errors[0].__typename;
  });
};

const isAvoidLoading = ref(false);

const cancelBook = async () => {
  isAvoidLoading.value = true;

  const result = await avoidBook(orderId.value);

  if (result?.errors) {
    await processBookActionError(result?.errors);
  }

  isAvoidLoading.value = false;
};

const isAcceptLoading = ref(false);

const acceptBook = async () => {
  isAcceptLoading.value = true;

  const result = await confirmBook(orderId.value);

  if (result?.errors) {
    await processBookActionError(result?.errors);
  }

  isAcceptLoading.value = false;
};

const isSendLoading = ref(false);

const sendBook = async () => {
  isSendLoading.value = true;

  await sendOrderToSupplier(orderId.value);

  isSendLoading.value = false;
};
</script>

<style scoped lang="scss"></style>
